import { useId, useState } from "react";

import { customFoodByCode } from "../domain/customFoods";
import { addDays, clockTime, dayLabel, todayStr } from "../domain/date";
import { ESTIMATE_ENTRY, editedEntryId, isEstimatedEntry, planStem, reportion } from "../domain/foodView";
import { MEAL_LABEL, MICRO_KEYS, OIL_KCAL_PER_TSP, entriesFor, totalsOf } from "../domain/intake";
import type { FoodEntry, MealSlot } from "../domain/types";
import { useKaya } from "../store/kayaStore";
import { addFoodEntry, copyEntryTo, moveEntryMeal, removeFoodEntry, saveTemplate } from "../store/toggles";
import { ActionBar } from "./ActionBar";
import { Button, IconButton } from "./Button";
import { EstimateLegend, Kcal } from "./Estimate";
import { I } from "./icons";
import { NavRow, RowGroup } from "./Rows";
import { Segmented } from "./Segmented";
import { Sheet } from "./Sheet";
import "./Plan.css";

const OILS = [0, 1, 2, 3].map((n) => ({ value: n, label: n === 0 ? "None" : `${n} tsp` }));

const one = (n: number) => (Math.round(n * 10) / 10).toLocaleString();
const g1 = (n: number | undefined) => (typeof n === "number" ? `${one(n)} g` : "–");
const mg1 = (n: number | undefined) => (typeof n === "number" ? `${one(n)} mg` : "–");
const mg = (n: number | undefined) => (typeof n === "number" ? `${Math.round(n).toLocaleString()} mg` : "–");

/**
 * One logged entry: how much, the oil, and every nutrient it carries.
 *
 * An edit is a tombstone plus a new id (ADR-026) — entries are immutable, and changing one in
 * place would lose to the other phone's copy on the next merge. The new values are scaled from the
 * entry's own frozen figures, never re-read from the table (ADR-025), and a nutrient it never had
 * shows "–", not 0 (invariant 18).
 */
export function FoodEntrySheet({
  entry,
  ds,
  onClose,
}: {
  entry: FoodEntry;
  ds: string;
  onClose: () => void;
}) {
  const { S, update, showToast } = useKaya();
  const dish = !!entry.code && customFoodByCode(S, entry.code)?.dishG !== undefined;
  const [qty, setQty] = useState(String(entry.qty));
  const [oil, setOil] = useState(entry.oilTsp || 0);
  const howId = useId();

  const n = Number(qty);
  const cur = Number.isFinite(n) ? n : 0;
  const grams = entry.unit === "g";
  const step = grams ? 10 : 0.5;
  const nudge = (d: number) => setQty(String(Math.max(0, Math.round((cur + d) * 10) / 10)));
  // Figures for the portion on screen; the id is minted only on Save.
  const shown = reportion(entry, { qty: cur, oilTsp: oil, id: entry.id });
  const changed = cur !== entry.qty || oil !== (entry.oilTsp || 0);
  const noMinerals = !MICRO_KEYS.some((k) => typeof entry[k] === "number");
  const fromPlan = entry.id.startsWith("p:");
  const estimated = isEstimatedEntry(entry);

  const save = () => {
    let saved = false;
    update((d) => {
      const L = d.logs[ds];
      const taken = new Set([...(L?.food || []).map((e) => e.id), ...Object.keys(L?.foodOff || {})]);
      const now = Date.now();
      const fresh = reportion(entry, {
        qty: cur,
        oilTsp: oil,
        id: editedEntryId(planStem(d, ds, entry.id), taken, now),
      });
      if (!fresh || !entriesFor(d, ds).some((e) => e.id === entry.id)) return;
      removeFoodEntry(d, ds, entry.id, now);
      addFoodEntry(d, ds, fresh);
      saved = true;
    });
    showToast(saved ? `${entry.name} · ${shown?.kcal.toLocaleString() ?? "–"} kcal` : "That entry has changed elsewhere");
    onClose();
  };

  const remove = () => {
    update((d) => removeFoodEntry(d, ds, entry.id));
    showToast(`Deleted ${entry.name}`);
    onClose();
  };

  const f = shown ?? entry;
  return (
    <Sheet
      title={entry.name}
      sub={`${MEAL_LABEL[entry.meal]} · logged ${clockTime(entry.at)}${fromPlan ? " from the plan" : ""}`}
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{
            label: "Save",
            onClick: save,
            disabled: !changed || !shown,
            // Kit §28: a disabled button says why.
            reason: !shown ? "Enter how much first" : !changed ? "Nothing changed yet" : undefined,
          }}
        />
      }
    >
      {/* Under the sub-line, as the kit draws it; the Sheet's sub is a <p> and cannot hold this one. */}
      {estimated ? <EstimateLegend>{ESTIMATE_ENTRY}</EstimateLegend> : null}
      <span className="k-food-label" id={howId}>
        How much
      </span>
      <div className="k-food-stepper" role="group" aria-labelledby={howId}>
        <IconButton icon={I.minus} label={`Less, by ${step} ${entry.unit}`} onClick={() => nudge(-step)} />
        <input
          className="k-food-input k-food-qty"
          type="number"
          inputMode="decimal"
          step={step}
          min="0"
          aria-label={`How much, in ${entry.unit}`}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
        />
        <IconButton icon={I.plus} label={`More, by ${step} ${entry.unit}`} onClick={() => nudge(step)} />
        <span className="k-food-note">
          {entry.unit}
          {!grams && shown ? ` · ${shown.grams} g` : ""}
        </span>
      </div>

      {dish && !entry.oilTsp ? (
        // A dish of her own was weighed with its oil in the pot (ADR-048); asking again would count it twice.
        <p className="k-food-note">The oil and ghee are in your dish already.</p>
      ) : (
        <>
          <span className="k-food-label">Oil or ghee added</span>
          <Segmented ariaLabel="Oil or ghee added" value={oil} onChange={setOil} options={OILS} />
          <p className="k-food-note">Each teaspoon adds {OIL_KCAL_PER_TSP} kcal. A tablespoon is three.</p>
        </>
      )}

      <RowGroup ariaLabel="Nutrients in this entry">
        <NavRow kind="value" title="Energy" value={shown ? <Kcal kcal={f.kcal} estimated={estimated} /> : "–"} />
        <NavRow kind="value" title="Protein" value={shown ? g1(f.p) : "–"} />
        <NavRow kind="value" title="Carbohydrate" value={shown ? g1(f.c) : "–"} />
        <NavRow kind="value" title={oil ? "Fat, with the oil" : "Fat"} value={shown ? g1(f.f) : "–"} />
        <NavRow kind="value" title="Fibre" value={shown ? g1(f.fb) : "–"} />
        <NavRow kind="value" title="Saturated fat" value={shown ? g1(f.sf) : "–"} />
        <NavRow kind="value" title="Sodium" value={shown ? mg(f.na) : "–"} />
        <NavRow kind="value" title="Calcium" value={shown ? mg(f.ca) : "–"} />
        <NavRow kind="value" title="Iron" value={shown ? mg1(f.fe) : "–"} />
        <NavRow kind="value" title="Potassium" value={shown ? mg(f.pot) : "–"} />
      </RowGroup>
      {noMinerals ? (
        <p className="k-food-note">
          No minerals are on record for this entry, so those read &ldquo;–&rdquo;, not 0.
        </p>
      ) : null}

      <CopyOrMove entry={entry} ds={ds} onDone={onClose} />

      <Button variant="danger" onClick={remove}>
        Delete this {entry.name} entry
      </Button>
    </Sheet>
  );
}

const MOVE_MEALS: { value: MealSlot; label: string }[] = [
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "snack", label: "Snack" },
  { value: "dinner", label: "Dinner" },
];

/**
 * Copy or move (FoodEntryMove, plan §9 row 3): the entry copied to any day of the week around —
 * a day ahead gets it planned, today or yesterday eaten — moved to another meal as a tombstone and
 * a new id, or its whole meal saved as a template. Every write is a writer in `store/toggles.ts`.
 */
function CopyOrMove({ entry, ds, onDone }: { entry: FoodEntry; ds: string; onDone: () => void }) {
  const { S, update, showToast } = useKaya();
  const today = todayStr();
  const days = [-1, 0, 1, 2, 3, 4, 5, 6].map((i) => addDays(today, i)).filter((x) => x !== ds);
  const [to, setTo] = useState(days.find((x) => x > ds) ?? days[0]);
  const [name, setName] = useState("");
  const mealWord = MEAL_LABEL[entry.meal].toLowerCase().replace(/s$/, "");
  const siblings = entriesFor(S, ds).filter((e) => e.meal === entry.meal);
  const st = totalsOf(siblings);
  const copyId = useId();
  const tplId = useId();

  return (
    <div className="k-entry-move">
      <h3 className="k-food-subhead">Copy or move</h3>
      <div className="k-food-form">
        <label className="k-food-label" htmlFor={copyId}>
          Copy to another day
        </label>
        <div className="k-food-row">
          <select id={copyId} className="k-food-input" value={to} onChange={(e) => setTo(e.target.value)}>
            {days.map((x) => (
              <option key={x} value={x}>
                {dayLabel(x)}, {mealWord}
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            ariaLabel={`Copy to ${dayLabel(to)}, ${mealWord}`}
            onClick={() => {
              update((d) => copyEntryTo(d, entry, to, entry.meal, today));
              showToast(`${entry.name} ${to > today ? "planned for" : "copied to"} ${dayLabel(to)}`);
            }}
          >
            Copy
          </Button>
        </div>
      </div>
      <div className="k-food-form">
        <span className="k-food-label">Move to another meal</span>
        <Segmented
          ariaLabel="Move to another meal"
          value={entry.meal}
          options={MOVE_MEALS}
          onChange={(m) => {
            if (m === entry.meal) return;
            let moved = false;
            update((d) => {
              moved = moveEntryMeal(d, ds, entry.id, m);
            });
            showToast(moved ? `${entry.name} moved to ${MEAL_LABEL[m].toLowerCase()}` : "That entry has changed elsewhere");
            onDone();
          }}
        />
        <p className="k-food-note">Same portion; only the meal changes.</p>
      </div>
      <div className="k-food-form">
        <label className="k-food-label" htmlFor={tplId}>
          Save this {mealWord} as a template
        </label>
        <div className="k-food-row">
          <input
            id={tplId}
            className="k-food-input"
            value={name}
            placeholder={`My ${mealWord}`}
            onChange={(e) => setName(e.target.value)}
          />
          <Button
            variant="ghost"
            disabled={!name.trim()}
            ariaLabel={`Save ${mealWord} as the template ${name.trim()}`}
            onClick={() => {
              update((d) => {
                saveTemplate(d, { name, items: siblings });
              });
              showToast(`Saved ${name.trim()}`);
              setName("");
            }}
          >
            Save
          </Button>
        </div>
        <p className="k-food-note">
          {siblings.length === 1 ? "This food" : siblings.length === 2 ? "Both foods" : `All ${siblings.length} foods`} ·{" "}
          {st.kcal.toLocaleString()} kcal · P {Math.round(st.p)} — use it on any day from the plan.
        </p>
      </div>
    </div>
  );
}
